import { EditOutlined, RestartAltOutlined, TuneOutlined } from '@mui/icons-material';
import {
    Button,
    Checkbox,
    Chip,
    IconButton,
    Paper,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';

import { BillingScheduleBar, BillingScheduleChips } from './billingSchedule';
import Result from 'components/v2/common/Result';

import { ACTIONS } from 'constant';
import { getServiceBilling } from 'helpers/serviceProviderBilling';
import useTranslate from 'hooks/useTranslate';
import type {
    BillingModalData,
    BillingRuleSource,
    ServiceProviderBillingConfig,
    ServiceRecord,
} from 'types';
import { formatCurrency } from 'utils/formatters';
import { localizedServiceName } from 'utils/servicesDocuments';

type HandleBillingAction = (action: ACTIONS, data: BillingModalData) => void;

const COLUMN_COUNT = 6;

const SOURCE_CHIPS: Record<
    BillingRuleSource,
    { labelKey: string; color: 'primary' | 'default' | 'warning' }
> = {
    custom: { labelKey: 'serviceProviderBilling.source.custom', color: 'primary' },
    default: { labelKey: 'serviceProviderBilling.source.default', color: 'default' },
    none: { labelKey: 'notConfigured', color: 'warning' },
};

const headerCellSx = { fontWeight: 700 };

interface ServiceBillingRowProps {
    service: ServiceRecord;
    config: ServiceProviderBillingConfig | null;
    language: string | undefined;
    selected: boolean;
    onToggle: (serviceId: number) => void;
    handleAction: HandleBillingAction;
}

const ServiceBillingRow = ({
    service,
    config,
    language,
    selected,
    onToggle,
    handleAction,
}: ServiceBillingRowProps) => {
    const { translate } = useTranslate();
    const { rule, source } = getServiceBilling(service.id, config);
    const name = localizedServiceName(service, language);
    const inherited = source === 'default';
    const sourceChip = SOURCE_CHIPS[source];
    const data: BillingModalData = { target: 'services', serviceIds: [service.id] };

    return (
        <TableRow hover selected={selected}>
            <TableCell padding="checkbox">
                <Checkbox
                    size="small"
                    checked={selected}
                    onChange={() => onToggle(service.id)}
                    slotProps={{ input: { 'aria-label': name } }}
                />
            </TableCell>
            <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {name}
                </Typography>
            </TableCell>
            <TableCell>
                <Chip
                    size="small"
                    variant="outlined"
                    color={sourceChip.color}
                    label={translate(sourceChip.labelKey)}
                />
            </TableCell>
            <TableCell>
                <Typography
                    variant="body2"
                    color={inherited ? 'text.secondary' : 'text.primary'}
                    sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
                >
                    {rule ? formatCurrency(rule.price, rule.currency) : '—'}
                </Typography>
            </TableCell>
            <TableCell sx={{ minWidth: 280 }}>
                {rule ? (
                    <Stack spacing={0.75}>
                        <BillingScheduleBar installments={rule.installments} muted={inherited} />
                        <BillingScheduleChips rule={rule} muted={inherited} />
                    </Stack>
                ) : (
                    '—'
                )}
            </TableCell>
            <TableCell align="right">
                <Stack direction="row" spacing={0.5} sx={{ justifyContent: 'flex-end' }}>
                    <Tooltip title={translate('serviceProviderBilling.customize')}>
                        <IconButton
                            size="small"
                            aria-label={translate('serviceProviderBilling.customize')}
                            onClick={() => handleAction(ACTIONS.EDIT, data)}
                        >
                            <EditOutlined fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={translate('serviceProviderBilling.resetToDefault')}>
                        <span>
                            <IconButton
                                size="small"
                                aria-label={translate('serviceProviderBilling.resetToDefault')}
                                disabled={source !== 'custom'}
                                onClick={() => handleAction(ACTIONS.DELETE, data)}
                            >
                                <RestartAltOutlined fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>
            </TableCell>
        </TableRow>
    );
};

interface ServicesBillingTableProps {
    loading: boolean;
    services: ServiceRecord[];
    config: ServiceProviderBillingConfig | null;
    language: string | undefined;
    selectedIds: number[];
    onSelectionChange: (serviceIds: number[]) => void;
    handleAction: HandleBillingAction;
}

const ServicesBillingTable = ({
    loading,
    services,
    config,
    language,
    selectedIds,
    onSelectionChange,
    handleAction,
}: ServicesBillingTableProps) => {
    const { translate } = useTranslate();
    const allSelected = services.length > 0 && selectedIds.length === services.length;
    const someSelected = selectedIds.length > 0 && !allSelected;

    const toggleAll = () =>
        onSelectionChange(allSelected ? [] : services.map((service) => service.id));

    const toggleOne = (serviceId: number) =>
        onSelectionChange(
            selectedIds.includes(serviceId)
                ? selectedIds.filter((id) => id !== serviceId)
                : [...selectedIds, serviceId]
        );

    const body = (() => {
        if (loading) {
            return Array.from({ length: 4 }).map((_, rowIndex) => (
                <TableRow key={`billing-skeleton-${rowIndex}`}>
                    {Array.from({ length: COLUMN_COUNT }).map((__, colIndex) => (
                        <TableCell key={colIndex}>
                            <Skeleton variant="rounded" height={24} width="80%" />
                        </TableCell>
                    ))}
                </TableRow>
            ));
        }
        if (!services.length) {
            return (
                <TableRow>
                    <TableCell colSpan={COLUMN_COUNT} sx={{ p: 0, borderBottom: 'none' }}>
                        <Result
                            title={translate('serviceProviderBilling.noServicesTitle')}
                            description={translate('serviceProviderBilling.noServicesDescription')}
                            sx={{ border: 'none', borderRadius: 0, background: 'transparent' }}
                        />
                    </TableCell>
                </TableRow>
            );
        }
        return services.map((service) => (
            <ServiceBillingRow
                key={service.id}
                service={service}
                config={config}
                language={language}
                selected={selectedIds.includes(service.id)}
                onToggle={toggleOne}
                handleAction={handleAction}
            />
        ));
    })();

    return (
        <Stack spacing={1.5}>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                sx={{
                    justifyContent: 'space-between',
                    alignItems: { xs: 'stretch', sm: 'center' },
                }}
            >
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {translate('serviceProviderBilling.services')}
                    </Typography>
                    {loading ? null : <Chip size="small" label={services.length} />}
                </Stack>
                <Button
                    size="small"
                    variant="contained"
                    startIcon={<TuneOutlined />}
                    disabled={!selectedIds.length}
                    onClick={() =>
                        handleAction(ACTIONS.EDIT, { target: 'services', serviceIds: selectedIds })
                    }
                    sx={{ textTransform: 'none' }}
                >
                    {`${translate('serviceProviderBilling.customizeSelected')}${
                        selectedIds.length ? ` (${selectedIds.length})` : ''
                    }`}
                </Button>
            </Stack>

            <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                    borderRadius: 3,
                    borderColor: 'rgba(148, 163, 184, 0.24)',
                    overflowX: 'auto',
                }}
            >
                <Table size="small" sx={{ minWidth: 860 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    size="small"
                                    checked={allSelected}
                                    indeterminate={someSelected}
                                    disabled={loading || !services.length}
                                    onChange={toggleAll}
                                    slotProps={{ input: { 'aria-label': translate('selectAll') } }}
                                />
                            </TableCell>
                            <TableCell sx={headerCellSx}>{translate('service')}</TableCell>
                            <TableCell sx={headerCellSx}>
                                {translate('serviceProviderBilling.columns.billing')}
                            </TableCell>
                            <TableCell sx={headerCellSx}>{translate('price')}</TableCell>
                            <TableCell sx={headerCellSx}>
                                {translate('serviceProviderBilling.columns.schedule')}
                            </TableCell>
                            <TableCell align="right" sx={headerCellSx}>
                                {translate('actions')}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>{body}</TableBody>
                </Table>
            </TableContainer>
        </Stack>
    );
};

export default ServicesBillingTable;
